import { useMemo, useState } from 'react';

import type { GraphDataset, GraphPerson } from '../data/graph-types.js';
import {
  focusedPeopleForPerson,
  peopleForListMode,
  type GraphIndex,
  type GraphPath,
} from '../lib/graph.js';
import type { FocusDepth, RelationshipFilter } from '../lib/relationship-view.js';

type ListViewProps = {
  dataset: GraphDataset;
  index: GraphIndex;
  selectedId: string | null;
  path: GraphPath | null;
  focusDepth: FocusDepth;
  relationshipFilters: readonly RelationshipFilter[];
  onSelect: (personId: string) => void;
};

function normalizedSearch(value: string): string {
  return value
    .normalize('NFKD')
    .replaceAll(/\p{Mark}/gu, '')
    .toLocaleLowerCase('en-US')
    .replaceAll(/\s+/gu, ' ')
    .trim();
}

export function listLetter(name: string): string {
  const first = normalizedSearch(name)[0];
  return first === undefined || !/[a-z]/u.test(first) ? '#' : first.toLocaleUpperCase('en-US');
}

export function filterListPeople(
  people: readonly GraphPerson[],
  query: string,
): readonly GraphPerson[] {
  const normalizedQuery = normalizedSearch(query);
  if (normalizedQuery.length === 0) {
    return people;
  }
  return people.filter((person) =>
    normalizedSearch(
      [person.name, ...(person.aliases ?? []), ...(person.searchKeys ?? [])].join(' '),
    ).includes(normalizedQuery),
  );
}

function PersonRows({
  people,
  index,
  selectedId,
  pathPeople,
  onSelect,
}: {
  people: readonly GraphPerson[];
  index: GraphIndex;
  selectedId: string | null;
  pathPeople: ReadonlySet<string>;
  onSelect: (personId: string) => void;
}) {
  return (
    <ul className="people-list">
      {people.map((person) => {
        const counterparts = index.counterpartsByPerson.get(person.id) ?? [];
        return (
          <li key={person.id}>
            <button
              className="person-list-row"
              data-selected={person.id === selectedId || undefined}
              data-path={pathPeople.has(person.id) || undefined}
              type="button"
              onClick={() => onSelect(person.id)}
            >
              <span className="person-list-row__star" aria-hidden="true" />
              <span className="person-list-row__name">{person.name}</span>
              {person.inclusionRole === 'context' ? (
                <span className="context-badge">Context</span>
              ) : null}
              <span className="person-list-row__count">
                {counterparts.length.toLocaleString('en-US')} connection
                {counterparts.length === 1 ? '' : 's'}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export function ListView({
  dataset,
  index,
  selectedId,
  path,
  focusDepth,
  relationshipFilters,
  onSelect,
}: ListViewProps) {
  const [query, setQuery] = useState('');
  const pathPeople = useMemo(() => new Set(path?.people ?? []), [path]);
  const people = useMemo(
    () =>
      peopleForListMode({
        dataset,
        index,
        selectedId,
        path,
        focusDepth,
        relationshipFilters,
      }).sort((left, right) => left.name.localeCompare(right.name)),
    [dataset, focusDepth, index, path, relationshipFilters, selectedId],
  );
  const filteredPeople = useMemo(() => filterListPeople(people, query), [people, query]);
  const filteredIds = useMemo(
    () => new Set(filteredPeople.map((person) => person.id)),
    [filteredPeople],
  );
  const focusedPeople = useMemo(() => {
    const orderedIds: string[] = [];
    const seen = new Set<string>();
    const add = (personId: string) => {
      if (!seen.has(personId) && filteredIds.has(personId)) {
        seen.add(personId);
        orderedIds.push(personId);
      }
    };
    if (selectedId !== null) {
      add(selectedId);
    }
    for (const personId of path?.people ?? []) {
      add(personId);
    }
    if (selectedId !== null) {
      const direct = focusedPeopleForPerson({
        index,
        personId: selectedId,
        depth: 'direct',
        relationshipFilters,
      });
      [...direct.keys()]
        .filter((personId) => personId !== selectedId)
        .sort((left, right) => {
          const leftName = index.peopleById.get(left)?.name ?? left;
          const rightName = index.peopleById.get(right)?.name ?? right;
          return leftName.localeCompare(rightName);
        })
        .forEach(add);
    }
    return orderedIds
      .map((personId) => index.peopleById.get(personId))
      .filter((person): person is GraphPerson => person !== undefined);
  }, [filteredIds, index, path, relationshipFilters, selectedId]);
  const catalogGroups = useMemo(() => {
    const groups = new Map<string, GraphPerson[]>();
    for (const person of filteredPeople) {
      const letter = listLetter(person.name);
      const group = groups.get(letter) ?? [];
      group.push(person);
      groups.set(letter, group);
    }
    return [...groups].sort(([left], [right]) =>
      left === '#' ? 1 : right === '#' ? -1 : left.localeCompare(right),
    );
  }, [filteredPeople]);

  return (
    <section className="list-view" aria-label="Public figures in this connection set">
      <header className="list-view__header">
        <div>
          <p className="eyebrow">Accessible constellation</p>
          <h2>People and connections</h2>
        </div>
        <p>
          {people.length.toLocaleString('en-US')} people ·{' '}
          {dataset.relationships.length.toLocaleString('en-US')}{' '}
          {dataset.relationships.length === 1 ? 'relationship' : 'relationships'} in enabled lenses
        </p>
      </header>

      <div className="list-view__tools">
        <label>
          <span>Filter this list</span>
          <input
            type="search"
            value={query}
            placeholder="Name or alias"
            onChange={(event) => setQuery(event.currentTarget.value)}
          />
        </label>
        <nav className="list-view__alphabet" aria-label="Jump through the people catalog">
          {catalogGroups.map(([letter]) => (
            <a key={letter} href={`#people-${letter === '#' ? 'other' : letter}`}>
              {letter}
            </a>
          ))}
        </nav>
      </div>

      {focusedPeople.length === 0 ? null : (
        <section className="list-view__focused" aria-labelledby="focused-people-heading">
          <div className="list-view__section-heading">
            <h3 id="focused-people-heading">Focused web</h3>
            <p>Selected person, active path, and direct connections</p>
          </div>
          <PersonRows
            people={focusedPeople}
            index={index}
            selectedId={selectedId}
            pathPeople={pathPeople}
            onSelect={onSelect}
          />
        </section>
      )}

      <section className="list-view__catalog" aria-labelledby="people-catalog-heading">
        <div className="list-view__section-heading">
          <h3 id="people-catalog-heading">Complete catalog</h3>
          <p>
            {filteredPeople.length.toLocaleString('en-US')}{' '}
            {filteredPeople.length === 1 ? 'match' : 'matches'}
          </p>
        </div>
        {catalogGroups.length === 0 ? (
          <p className="list-view__empty">No people match this list filter.</p>
        ) : (
          catalogGroups.map(([letter, group]) => (
            <section
              className="people-letter-group"
              key={letter}
              aria-labelledby={`people-${letter === '#' ? 'other' : letter}`}
            >
              <h4 id={`people-${letter === '#' ? 'other' : letter}`}>{letter}</h4>
              <PersonRows
                people={group}
                index={index}
                selectedId={selectedId}
                pathPeople={pathPeople}
                onSelect={onSelect}
              />
            </section>
          ))
        )}
      </section>
    </section>
  );
}
